import { isBoutiqueSide } from "@/features/invoices/invoice-utils";
import {
  BankRemittanceReceiptFormFields
} from "@/features/invoices/bank-remittance-receipts/upload-bank-remittance-receipt-page";
import { FormikProps } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload } from "react-router-dom";
import BinarySplitView from "@/features/ui/binary-split-view";
import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import InvoiceFileFormControl from "@/features/invoices/order-confirmation-invoices/invoice-file-form-control";
import BankRemittanceReceiptFigureFormControl
  from "@/features/invoices/bank-remittance-receipts/bank-remittance-receipt-figure-form-control";
import BankRemittanceReceiptInformationFormControlModal
  from "@/features/invoices/bank-remittance-receipts/bank-remittance-receipt-information-form-control-modal";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import AppButton from "@/features/line-sheet-sets/app-button";
import useParseBankReceipt, {
  ParseBankReceiptResponse,
} from "@/features/invoices/bank-remittance-receipts/hooks/use-parse-bank-receipt";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface BankRemittanceReceiptFormModalProps {
  formik: FormikProps<BankRemittanceReceiptFormFields>;
  isEditing?: boolean;
}

function ParsedBankReceiptTable({
  parsed,
  isParsing,
  onApply,
  isEditing,
}: {
  parsed: ParseBankReceiptResponse | null;
  isParsing: boolean;
  onApply: () => void;
  isEditing: boolean;
}) {
  if (!isParsing && !parsed) {
    return null;
  }

  const krw = (v?: number) =>
    v != null ? "₩" + Math.round(v).toLocaleString() : "-";
  const fmtDate = (d: Date | string | null) => {
    if (d instanceof Date) {
      return isNaN(d.getTime()) ? "-" : d.toISOString().slice(0, 10);
    }
    return d ? String(d) : "-";
  };
  const rows: [string, string][] = parsed
    ? [
        ["통화", parsed.currency || "-"],
        [
          "외화 금액",
          parsed.amount != null
            ? `${parsed.currency} ${parsed.amount.toLocaleString()}`
            : "-",
        ],
        ["환율", parsed.exchangeRate ? parsed.exchangeRate.toLocaleString() : "-"],
        ["송금 원화 금액", krw(parsed.amountKrw)],
        ["은행 수수료", krw(parsed.bankFee)],
        ["총 징수 원화 금액", krw(parsed.totalAmountKrw)],
        ["거래일자", fmtDate(parsed.issuedOn)],
      ]
    : [];

  return (
    <AppPanel title={"영수증 추출 정보"}>
      <Flex flexDirection={"column"} gap={"8px"}>
        {isParsing && (
          <Text fontSize={"12px"} color={"#6F6F6C"}>
            영수증에서 정보를 추출하는 중…
          </Text>
        )}
        {parsed && (
          <>
            <Grid templateColumns={"1fr 1fr"} rowGap={"4px"} columnGap={"8px"}>
              {rows.map(([label, value]) => (
                <Box key={label} display={"contents"}>
                  <Text fontSize={"12px"} color={"#6F6F6C"}>
                    {label}
                  </Text>
                  <Text fontSize={"12px"} fontWeight={"600"} textAlign={"right"}>
                    {value}
                  </Text>
                </Box>
              ))}
            </Grid>
            <Text fontSize={"11px"} color={"#6F6F6C"}>
              송금일자는 자동 입력되었습니다. 환율·은행수수료는 아래 버튼으로 적용하세요. (금액은 직접 입력)
            </Text>
            <AppButton
              variant={"primary"}
              size={"small"}
              isDisabled={!isEditing}
              onClick={onApply}
            >
              환율·수수료 적용
            </AppButton>
          </>
        )}
      </Flex>
    </AppPanel>
  );
}

export default function BankRemittanceReceiptFormModal({
  formik,
  isEditing = false,
}: BankRemittanceReceiptFormModalProps) {
  const { error: showError, info: showInfo } = useAppToasts();
  const { fire: fireParseBankReceipt } = useParseBankReceipt();
  const [parsed, setParsed] = useState<ParseBankReceiptResponse | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const lastParsedFileRef = useRef<File | null>(null);
  const { tTitle } = useI18nHelper();

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (formik.isSubmitting || (formik.dirty && isEditing)) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    [formik.isSubmitting, formik.dirty, isEditing]
  );

  useBeforeUnload(handleBeforeUnload);

  useEffect(() => {
    //수정 취소시 기본값으로 돌리기
    if (!formik.isSubmitting && formik.dirty && !isEditing) {
      formik.resetForm();
    }
  }, [isEditing, formik]);

  // 파일 첨부 시 은행영수증 PDF를 백엔드로 보내 정보 추출.
  // 송금일자는 자동 입력하고, 나머지는 표로 보여주며 금액 등은 버튼으로만 반영한다.
  useEffect(() => {
    // 해외 송금(수신사가 부티크·3자무역사)일 때만 영수증 파싱한다. 국내 송금은 영수증 양식이 달라 파싱하지 않는다.
    if (!isBoutiqueSide(formik.values.receiver?.type)) {
      return;
    }
    const file = formik.values.file;
    if (!(file instanceof File)) {
      return;
    }
    if (lastParsedFileRef.current === file) {
      return;
    }
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      return;
    }
    lastParsedFileRef.current = file;
    // 파일이 바뀌면 기존 추출 정보를 먼저 비우고, API 응답을 기다려 새 값으로 채운다.
    setParsed(null);
    setIsParsing(true);

    let cancelled = false;
    (async () => {
      try {
        const res = await fireParseBankReceipt({}, { file });
        if (cancelled || !res) {
          return;
        }
        setParsed(res);
        if (res.issuedOn) {
          formik.setFieldValue("issuedOn", new Date(res.issuedOn));
        }
        if (res.refNo) {
          // Ref No 는 BRR invoice_number(number)로 저장한다.
          formik.setFieldValue("number", res.refNo);
        }
      } catch (e) {
        showError(e);
      } finally {
        if (!cancelled) {
          setIsParsing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.file, formik.values.receiver?.type]);

  const handleApplyParsed = useCallback(() => {
    if (!parsed) {
      return;
    }
    // 금액은 현재 수정 불가하므로 환율·은행수수료만 적용한다.
    formik.setValues((prev) => ({
      ...prev,
      exchangeRate: parsed.exchangeRate || prev.exchangeRate,
      bankFee:
        parsed.bankFee != null
          ? { value: parsed.bankFee, currency: "KRW" }
          : prev.bankFee,
    }));
    showInfo("추출된 환율·은행수수료를 적용했습니다.");
  }, [parsed, formik, showInfo]);

  return (
    <Box padding={"24px"} overflow={"hidden"}>
      <BinarySplitView
        left={
          <Flex flexDirection={"column"} gap={"24px"}>
            <BankRemittanceReceiptInformationFormControlModal
              formik={formik}
              isEditing={isEditing}
            />
            <InvoiceFileFormControl
              formik={formik}
              isEditing={isEditing}
              isDisabled={isEditing && formik.values.id !== -1}
              title={tTitle("bank_remittance_receipt_file")}
            />
            <ParsedBankReceiptTable
              parsed={parsed}
              isParsing={isParsing}
              onApply={handleApplyParsed}
              isEditing={isEditing}
            />
          </Flex>
        }
        right={
          <Flex flexDirection={"column"} gap={"24px"}>
            <BankRemittanceReceiptFigureFormControl
              formik={formik}
              isEditing={isEditing}
            />
          </Flex>
        }
        minLeftWidth={400}
        minRightWidth={400}
      />
    </Box>
  );
}
